<template>
  <div class="theme-doughnut-chart">
    <Doughnut
      :chart-data="chartData"
      :chart-options="chartOptions"
    />
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export default {
  name: 'ThemeDoughnutChart',
  components: { Doughnut },
  
  props: {
    data: {
      type: Array,
      required: true
    },
    showPercentages: {
      type: Boolean,
      default: true
    },
    showLegend: {
      type: Boolean,
      default: true
    },
    cutout: {
      type: String,
      default: '60%'
    },
    colors: {
      type: Array,
      default: null
    }
  },

  data() {
    return {
      isDarkMode: document.documentElement.classList.contains('dark'),
      themeObserver: null
    };
  },

  mounted() {
    // Watch for theme changes
    this.themeObserver = new MutationObserver(() => {
      this.isDarkMode = document.documentElement.classList.contains('dark');
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  computed: {
    chartData() {
      if (!this.data || !Array.isArray(this.data) || this.data.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      return {
        labels: this.data.map(item => item.label),
        datasets: [{
          data: this.data.map(item => parseFloat(item.value)),
          backgroundColor: this.colors || this.getColors(),
          borderWidth: 0
        }]
      };
    },

    chartOptions() {
      const textColor = this.isDarkMode ? '#94a3b8' : '#374151';
      const tooltipBg = this.isDarkMode ? '#1e293b' : '#ffffff';
      const tooltipBorder = this.isDarkMode ? '#334155' : '#e5e7eb';

      return {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        cutout: this.cutout,
        plugins: {
          legend: {
            display: this.showLegend,
            position: 'bottom',
            labels: {
              color: textColor,
              padding: 12,
              font: {
                size: 13,
                weight: '500'
              },
              usePointStyle: true,
              pointStyle: 'rect',
              boxWidth: 12,
              boxHeight: 12
            }
          },
          tooltip: {
            enabled: true,
            backgroundColor: tooltipBg,
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: tooltipBorder,
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            bodyFont: {
              size: 14,
              weight: '500'
            },
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                
                if (this.showPercentages) {
                  return `${label}: ${value.toFixed(2)} (${percentage}%)`;
                }
                return `${label}: ${value.toFixed(2)}`;
              }
            }
          },
          datalabels: false
        },
        layout: {
          padding: {
            top: 10,
            bottom: 10
          }
        }
      };
    }
  },

  methods: {
    getColors() {
      // Color palette matching Figma design
      return [
        '#3b82f6', // blue
        '#8b5cf6', // purple
        '#14b8a6', // teal/cyan
        '#f97316', // orange
        '#10b981', // green
        '#ef4444', // red
        '#ec4899', // pink
        '#f59e0b', // amber
        '#06b6d4', // cyan
        '#f43f5e'  // rose
      ];
    }
  }
};
</script>

